import { useCallback, useEffect, useState } from 'react';

import { removeMessageListener } from '@dvag/dfs-orchestrator-client';
import { addCustomMessageListener } from '@dvag/dfs-orchestrator-client/listeners';
import {
  navigate as orchestratorNavigate,
  setMicroappIsDirty,
} from '@dvag/dfs-orchestrator-client/messengers';

type Continuation = { onConfirm: () => void; onCancel: () => void } | undefined;
export type ContinutationRequest = () => void;
export type OnCanContinue = (a: boolean) => void;

type CanContinue = { id: string; value: boolean };

const HEX_BASE = 16;
const TIMEOUT_DURATION_MS = 300;
const INITIAL_DELAY_MS = 100;

const updateCanContinueList = (prev: CanContinue[], id: string, canContinueCond: boolean) => {
  const index = prev.findIndex((el) => el.id === id);

  if (index === -1) {
    return [...prev, { id, value: canContinueCond }];
  }

  const newCanContinue = [...prev];
  newCanContinue[index] = { id, value: canContinueCond };

  return newCanContinue;
};

export const getGuid = () =>
  '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, () =>
    crypto.getRandomValues(new Uint8Array(1))[0].toString(HEX_BASE),
  );

export const useContinuation = () => {
  const [continuation, setContinuation] = useState<Continuation>(undefined);
  const [canContinueList, setCanContinueList] = useState<CanContinue[]>([]);

  const canContinue = canContinueList.every((item) => item.value);

  const [continuationRequestList, setContinuationRequestList] = useState<ContinutationRequest[]>(
    [],
  );

  const onContinuationRequest = useCallback((continuationRequestCallback: ContinutationRequest) => {
    setContinuationRequestList((previous) => [...previous, continuationRequestCallback]);
  }, []);

  const clearContinuation = useCallback(() => {
    setContinuation(undefined);
  }, []);

  const getConfirmHandler = useCallback(
    (continuationCallback: () => void) => () => {
      clearContinuation();
      setMicroappIsDirty(false);
      setTimeout(continuationCallback, TIMEOUT_DURATION_MS);
    },
    [clearContinuation],
  );

  const createContinuation = useCallback(
    (continuationCallback: () => void): Exclude<Continuation, undefined> => ({
      onConfirm: getConfirmHandler(continuationCallback),
      onCancel: clearContinuation,
    }),
    [clearContinuation, getConfirmHandler],
  );

  const getContinuationHandler = useCallback(
    (continuationCallback: () => void) => () => {
      continuationRequestList.forEach((request) => request());

      if (canContinue) {
        continuationCallback();
        return;
      }

      setTimeout(() => {
        setContinuation(createContinuation(continuationCallback));
      }, INITIAL_DELAY_MS);
    },
    [canContinue, continuationRequestList, createContinuation],
  );

  const getCanContinueHandler = useCallback(() => {
    const id = getGuid();

    return (canContinueCond: boolean) => {
      setCanContinueList((prev) => updateCanContinueList(prev, id, canContinueCond));
    };
  }, []);

  useEffect(() => {
    setMicroappIsDirty(!canContinue);

    if (continuation !== undefined && canContinue) {
      continuation.onConfirm();
    }
  }, [canContinue, continuation]);

  useEffect(() => {
    const customMsgId = addCustomMessageListener('navigationRequest', (data) => {
      const requestPayload = (data as { payload: Record<string, string> }).payload;
      const { url, nextUrl } = requestPayload;

      getContinuationHandler(() => orchestratorNavigate(url, nextUrl))();
    });

    return () => {
      removeMessageListener(customMsgId);
    };
  }, [getContinuationHandler]);

  return {
    continuation,
    getContinuationHandler,
    getCanContinueHandler,
    onContinuationRequest,
  };
};
